/**
 * Transform utilities - calculations, coordinates, and state management
 */

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================
/**
 * Calculate new scale based on handle position and mouse movement
 */
export const calculateCornerScale = (handlePosition, deltaX, deltaY, startScale, sensitivity = 0.005) => {
  let scaleChange = 0;

  switch (handlePosition) {
    case 'topLeft':
      scaleChange = -(deltaX + deltaY) / 2;
      break;
    case 'topRight':
      scaleChange = (deltaX - deltaY) / 2;
      break;
    case 'bottomLeft':
      scaleChange = (-deltaX + deltaY) / 2;
      break;
    case 'bottomRight':
      scaleChange = (deltaX + deltaY) / 2;
      break;
  }

  return Math.max(0.01, startScale + (scaleChange * sensitivity));
};

/**
 * Calculate new scale for side handles (non-proportional)
 */
export const calculateSideScale = (handlePosition, deltaX, deltaY, startScaleX, startScaleY, sensitivity = 0.005) => {
  let newScaleX = startScaleX;
  let newScaleY = startScaleY;

  switch (handlePosition) {
    case 'top':
      newScaleY = Math.max(0.01, startScaleY - (deltaY * sensitivity));
      break;
    case 'bottom':
      newScaleY = Math.max(0.01, startScaleY + (deltaY * sensitivity));
      break;
    case 'left':
      newScaleX = Math.max(0.01, startScaleX - (deltaX * sensitivity));
      break;
    case 'right':
      newScaleX = Math.max(0.01, startScaleX + (deltaX * sensitivity));
      break;
  }

  return { scaleX: newScaleX, scaleY: newScaleY };
};

/**
 * Calculate new rotation based on mouse movement
 */
export const calculateRotation = (rotationCenter, startPoint, currentPoint, startRotation) => {
  // Calculate angle from center to each point
  const startAngle = Math.atan2(
    startPoint.y - rotationCenter.y,
    startPoint.x - rotationCenter.x
  );
  const currentAngle = Math.atan2(
    currentPoint.y - rotationCenter.y,
    currentPoint.x - rotationCenter.x
  );

  // Calculate delta and normalize
  let deltaAngle = currentAngle - startAngle;
  while (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
  while (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

  return startRotation + deltaAngle;
};

/**
 * Calculate bounding box dimensions with padding
 */
export const calculateBoundingBox = (target, padding = 5) => {
  if (!target || typeof target.width === 'undefined' || typeof target.height === 'undefined') {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      centerX: 0,
      centerY: 0,
      rotation: target?.rotation || 0
    };
  }

  const halfWidth = target.width / 2;
  const halfHeight = target.height / 2;

  const x = target.x - halfWidth - padding;
  const y = target.y - halfHeight - padding;
  const width = target.width + padding * 2;
  const height = target.height + padding * 2;

  return {
    x,
    y,
    width,
    height,
    centerX: x + width / 2,
    centerY: y + height / 2,
    rotation: target.rotation
  };
};

/**
 * Calculate handle positions based on bounding box
 */
export const calculateHandlePositions = (boundingBox) => {
  const { x, y, width, height } = boundingBox;
  const left = x;
  const right = x + width;
  const top = y;
  const bottom = y + height;
  const centerX = (left + right) / 2;
  const centerY = (top + bottom) / 2;

  return {
    corners: {
      topLeft: { x: left, y: top },
      topRight: { x: right, y: top },
      bottomLeft: { x: left, y: bottom },
      bottomRight: { x: right, y: bottom }
    },
    sides: {
      top: { x: centerX, y: top },
      right: { x: right, y: centerY },
      bottom: { x: centerX, y: bottom },
      left: { x: left, y: centerY }
    },
    rotation: { x: centerX, y: top - 30 } // 30px above top
  };
};

/**
 * Check if transform has actually changed
 */
export const hasTransformChanged = (initial, final, threshold = 0.01) => {
  return Math.abs(final.scaleX - initial.scaleX) > threshold ||
    Math.abs(final.scaleY - initial.scaleY) > threshold ||
    Math.abs(final.rotation - initial.rotation) > threshold;
};

// ============================================================================
// COORDINATE HELPERS
// ============================================================================

/**
 * Convert screen coordinates to world coordinates
 */
export const screenToWorld = (viewport, screenX, screenY) => {
  if (!viewport) return { x: screenX, y: screenY };
  return viewport.toWorld(screenX, screenY);
};

/**
 * Get inverse scale for handle sizing
 */
export const getInverseScale = (viewport) => {
  if (!viewport) return 1;
  return 1 / viewport.scaled;
};

/**
 * Scale value by inverse viewport scale
 */
export const scaleByViewport = (value, viewport) => {
  return value * getInverseScale(viewport);
};

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

/**
 * Create initial transform state
 */
export const createInitialTransform = (target) => ({
  scaleX: target.scale.x,
  scaleY: target.scale.y,
  width: target.width,
  height: target.height,
  rotation: target.rotation
});

/**
 * Create final transform state
 */
export const createFinalTransform = (target) => ({
  scaleX: target.scale.x,
  scaleY: target.scale.y,
  width: target.width,
  height: target.height,
  rotation: target.rotation
});

/**
 * Create resize state for tracking transform operations
 */
export const createResizeState = (target, container, startPoint, handleType, handlePosition, additionalData = {}) => ({
  isResizing: true,
  target,
  container,
  startPoint,
  startScale: target.scale.x,
  startScaleY: target.scale.y,
  handleType,
  handlePosition,
  ...additionalData
});

/**
 * Apply transform to target and sync with interactive area
 */
export const applyTransform = (target, transform) => {
  if (transform.scaleX !== undefined && transform.scaleY !== undefined) {
    target.scale.set(transform.scaleX, transform.scaleY);

    if (target.text !== undefined && target.style !== undefined) {
      target.resolution = Math.max(2, window.devicePixelRatio || 2) * Math.max(transform.scaleX, transform.scaleY);
    }

    // Sync interactive area
    if (target.interactiveArea) {
      target.interactiveArea.scale.set(transform.scaleX, transform.scaleY);
    }
  }

  if (transform.rotation !== undefined) {
    target.rotation = transform.rotation;

    // Sync interactive area rotation
    if (target.interactiveArea) {
      target.interactiveArea.rotation = transform.rotation;
    }
  }
};